import React, { useState } from 'react';
import {
  Modal,
  Card,
  Typography,
  Space,
  Button,
  Row,
  Col,
  Progress,
  List,
  Tooltip,
  message,
  Tag,
  Descriptions,
  Radio,
  Select,
} from 'antd';
import {
  DollarOutlined,
  FileTextOutlined,
  ArrowUpOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { DataTable, MoneyCell, StatusCell, type MoneyType } from '../../ui';
import dayjs from 'dayjs';
import { Route } from '../../types';
import { formatRouteDirection } from '../../utils/addressUtils';
import RouteDocumentsModal from '../../components/routes/RouteDocumentsModal';
import {
  RouteApplicationsCompactText,
  RouteClientsCompactText
} from '../../features/routes/RouteMultiAppCompactCells';
import CreatePaymentModal from '../../components/finance/CreatePaymentModal';
import CreateRouteExpenseModal from '../../components/finance/CreateRouteExpenseModal';
import {
  useGetPaymentsListQuery,
  useGetRouteExpensesQuery,
  useGetRoutesExtendedQuery,
  useDeleteRouteExpenseMutation,
  useUpdateRouteExpenseStatusMutation,
  useSetRouteExpenseAllocationMutation,
  Payment,
  RouteExpense,
} from '../../store/api/financeApi';
import { obligationStageLabel } from '../../utils/obligationStageUi';
import { obligationTriggerCodeLabel, routeExpenseTypeLabel } from '../../utils/enumUiLabels';
import { FinanceSourceDataHint } from '../../utils/financeDomainUi';
import { useAuth } from '../../hooks/useAuth';

const { Text } = Typography;

export interface ObligationItem {
  id?: number;
  direction?: string;
  dueDate?: string | null;
  remainingAmount?: number;
  amountWithVAT?: number;
  totalPaid?: number;
  stage?: string;
  triggerCode?: string;
  triggerDate?: string | null;
  delayDays?: number | null;
  /** Заявка: оплата разрешена только после счёта (PREPAYMENT) / оригиналов (FINAL). Для маршрута не задаётся — считается true. */
  isActive?: boolean;
  status?: string;
}

interface ObligationEmbeddedPaymentRow {
  key: string;
  dateLabel: string;
  amount: number;
}

interface ObligationPaymentsBlockProps {
  obligation: ObligationItem;
  visible: boolean;
  moneyType: MoneyType;
  onAddPayment: () => void;
  /** Workspace drawer: без кнопки «Добавить платеж» */
  readOnly?: boolean;
  /** Workspace drawer: компактные поля сумма/остаток/срок/статус */
  compact?: boolean;
  /** Платежи из GET /applications/:id — без доп. запроса */
  embeddedPaymentRows?: ObligationEmbeddedPaymentRow[];
}

export interface ExtendedRoute extends Route {
  aggregatedStatus?: { overallStatus: 'OPEN' | 'PARTIAL' | 'OK' | 'OVERDUE' };
  payable?: { totalRequired: number; totalPaid: number; remaining: number; overdue: number };
  receivable?: { totalRequired: number; totalPaid: number; remaining: number; overdue: number };
  obligations?: ObligationItem[];
  incoming?: { total?: number };
  outgoing?: { total?: number };
}

export const ObligationPaymentsBlock: React.FC<ObligationPaymentsBlockProps> = ({
  obligation,
  visible,
  moneyType,
  onAddPayment,
  readOnly = false,
  compact = false,
  embeddedPaymentRows,
}) => {
  const amount = Number(obligation.amountWithVAT ?? 0);
  const totalPaid = Number(obligation.totalPaid ?? 0);
  const remaining = Number(obligation.remainingAmount ?? 0);
  const stageLabel = obligationStageLabel(obligation.stage);
  const isActive = obligation.isActive !== false;
  const delayDays = Math.max(0, Math.floor(Number(obligation.delayDays ?? 0)));
  const stageU = String(obligation.stage || '').toUpperCase();
  const payDisabledReason =
    moneyType === 'income' && !isActive
      ? stageU === 'FINAL'
        ? 'Платеж доступен после получения оригиналов'
        : 'Платеж доступен после выставления счёта'
      : null;

  const useEmbeddedPayments = embeddedPaymentRows !== undefined;

  const { data, isLoading } = useGetPaymentsListQuery(
    { obligationId: obligation.id!, limit: 100 },
    { skip: !obligation.id || !visible || useEmbeddedPayments }
  );

  const payments = (data?.payments ?? []).slice().sort((a, b) => {
    const da = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
    const db = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
    return da - db;
  });

  const hasExpenseTriggerDate =
    obligation.triggerDate != null && String(obligation.triggerDate) !== '';
  const awaitingEventForIncome = moneyType === 'income' && !isActive;
  const showTriggerDetails = moneyType === 'expense' ? hasExpenseTriggerDate : isActive;
  const triggerLabel = obligation.triggerCode
    ? obligationTriggerCodeLabel(obligation.triggerCode)
    : null;

  if (compact) {
    const datedPayment = embeddedPaymentRows?.find((p) => p.dateLabel !== 'Дата не указана');
    const paidAtLabel = totalPaid > 0.005 ? datedPayment?.dateLabel ?? '—' : '—';
    const dueLabel = obligation.dueDate
      ? dayjs(obligation.dueDate).format('DD.MM.YYYY')
      : '—';

    return (
      <div
        style={{
          padding: '8px 10px',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          marginBottom: 8,
          opacity: moneyType === 'income' && !isActive ? 0.55 : 1,
        }}
      >
        <Text strong style={{ fontSize: 13 }}>
          {stageLabel}
        </Text>
        <Descriptions size="small" column={1} style={{ marginTop: 6 }} labelStyle={{ width: 118 }}>
          <Descriptions.Item label="Сумма">
            <MoneyCell value={amount} type={moneyType} />
          </Descriptions.Item>
          <Descriptions.Item label="Остаток">
            <MoneyCell value={remaining} type={moneyType} />
          </Descriptions.Item>
          <Descriptions.Item label="Срок оплаты">{dueLabel}</Descriptions.Item>
          <Descriptions.Item label="Дата оплаты">
            {totalPaid > 0.005 ? paidAtLabel : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Статус">
            {obligation.status ? <StatusCell status={obligation.status} /> : '—'}
          </Descriptions.Item>
        </Descriptions>
        {embeddedPaymentRows && embeddedPaymentRows.length > 0 ? (
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
              Платежи:
            </Text>
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {embeddedPaymentRows.map((p) => (
                <Text key={p.key} style={{ fontSize: 12 }}>
                  {p.dateLabel} — <MoneyCell value={p.amount} type={moneyType} />
                </Text>
              ))}
            </Space>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 12,
        paddingTop: 12,
        borderTop: '1px solid #f0f0f0',
        opacity: moneyType === 'income' && !isActive ? 0.55 : 1,
      }}
    >
      <Text strong>
        {stageLabel} — <MoneyCell value={amount} type={moneyType} />
      </Text>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
        Отсрочка: {delayDays} дн.
      </Text>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
        {awaitingEventForIncome ? (
          <>Ожидание события</>
        ) : !showTriggerDetails ? (
          'Ожидание события'
        ) : (
          <>
            {triggerLabel != null && <span>Триггер: {triggerLabel}</span>}
            {triggerLabel != null && <br />}
            {obligation.triggerDate ? (
              <>
                <span>Дата события: {dayjs(obligation.triggerDate).format('DD.MM')}</span>
                <br />
              </>
            ) : null}
            <span>
              Срок оплаты:{' '}
              {obligation.dueDate ? dayjs(obligation.dueDate).format('DD.MM.YYYY') : '—'}
            </span>
          </>
        )}
      </Text>
      {(() => {
        const showBar =
          amount > 0 && (moneyType === 'expense' || (moneyType === 'income' && isActive));
        return showBar ? (
        <div style={{ marginTop: 6 }}>
          <Progress
            percent={amount > 0 ? Math.min(100, Math.round((totalPaid / amount) * 100)) : 0}
            size="small"
            showInfo={false}
            strokeColor={remaining > 0 ? '#faad14' : '#52c41a'}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Оплачено: <MoneyCell value={totalPaid} type={moneyType} /> · Остаток:{' '}
            <MoneyCell value={remaining} type={moneyType} />
          </Text>
        </div>
      ) : (
        <div style={{ marginTop: 6 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Оплачено: <MoneyCell value={totalPaid} type={moneyType} /> · Остаток:{' '}
            <MoneyCell value={remaining} type={moneyType} />
          </Text>
        </div>
      );
      })()}
      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Платежи</Text>
        {isLoading ? (
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>Загрузка...</Text>
        ) : payments.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>Платежей пока нет</Text>
        ) : (
          <List
            size="small"
            dataSource={payments}
            renderItem={(p: Payment) => (
              <List.Item style={{ padding: '2px 0', border: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span>
                    {p.paymentDate ? dayjs(p.paymentDate).format('DD.MM') : '—'}
                  </span>
                  <span>
                    <MoneyCell value={Number(p.amountPaid ?? p.amountToPay ?? 0)} type={moneyType} />
                  </span>
                </div>
              </List.Item>
            )}
            style={{ marginTop: 4 }}
          />
        )}
        {!readOnly && remaining > 0 && payDisabledReason != null && (
          <Tooltip title={payDisabledReason}>
            <span>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                disabled
                style={{ paddingLeft: 0, marginTop: 4 }}
              >
                Добавить платеж
              </Button>
            </span>
          </Tooltip>
        )}
        {!readOnly && remaining > 0 && payDisabledReason == null && (
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={onAddPayment}
            style={{ paddingLeft: 0, marginTop: 4 }}
          >
            Добавить платеж
          </Button>
        )}
      </div>
    </div>
  );
};

export interface FinanceRouteFinanceContentProps {
  visible: boolean;
  route: ExtendedRoute | null;
  onClose?: () => void;
  onRefetch?: () => void;
  /** Только тело панели + дочерние модалки (без Modal) — для встраивания в Drawer */
  embedded?: boolean;
}

export const FinanceRouteFinanceContent: React.FC<FinanceRouteFinanceContentProps> = ({
  visible,
  route,
  onClose,
  onRefetch,
  embedded = false,
}) => {
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  const [createPaymentModalVisible, setCreatePaymentModalVisible] = useState(false);
  const [createExpenseModalVisible, setCreateExpenseModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState<RouteExpense | null>(null);
  const [initialObligationId, setInitialObligationId] = useState<number | null>(null);
  const [paymentModalDirection, setPaymentModalDirection] = useState<'INCOME' | 'EXPENSE' | null>(null);
  const [allocateExpense, setAllocateExpense] = useState<RouteExpense | null>(null);
  const [allocateMode, setAllocateMode] = useState<'COMMON' | 'DIRECT'>('COMMON');
  const [allocateApplicationId, setAllocateApplicationId] = useState<number | null>(null);

  const { user } = useAuth();
  const role = String(user?.role?.name || '').toLowerCase();
  const canSeeDrafts = ['admin', 'accountant', 'logist'].includes(role);
  const canConfirmReject = ['admin', 'accountant'].includes(role);
  const canCreateExpense = ['admin', 'accountant', 'logist'].includes(role);
  const [deleteExpense, { isLoading: deletingExpense }] = useDeleteRouteExpenseMutation();
  const [setExpenseStatus, { isLoading: statusUpdating }] = useUpdateRouteExpenseStatusMutation();
  const [setExpenseAllocation, { isLoading: allocatingExpense }] = useSetRouteExpenseAllocationMutation();

  const { data: routeExpensesPayload, isLoading: loadingExpenses } = useGetRouteExpensesQuery({ routeId: route?.id ?? 0, includeDraft: canSeeDrafts, includeRejected: canConfirmReject }, {
    skip: !visible || !route?.id,
  });
  const routeExpenses = routeExpensesPayload?.expenses ?? [];

  const confirmedExpenses = routeExpenses.filter((e) => String(e.status || '').toUpperCase() === 'CONFIRMED');

  const { data: routesExtendedData } = useGetRoutesExtendedQuery(
    { page: 1, limit: 1, routeId: route?.id ?? 0 },
    { skip: !visible || !route?.id }
  );

  const totalExpenses = confirmedExpenses.reduce((sum, e) => sum + Number(e.amountWithVAT ?? 0), 0);

  if (!route) return null;

  const routeReadModelRow = routesExtendedData?.routes?.[0];
  const routeView: ExtendedRoute = routeReadModelRow
    ? {
        ...route,
        payable: routeReadModelRow.payable,
        aggregatedStatus: routeReadModelRow.aggregatedStatus,
        obligations: routeReadModelRow.obligations,
      }
    : route;

  const payObl = (routeView.obligations || []).filter((o) => String(o.direction).toUpperCase() === 'EXPENSE');
  const prepPay = payObl.find((o) => o.stage === 'PREPAYMENT');
  const finalPay = payObl.find((o) => o.stage === 'FINAL');

  const allocationCandidates = (route.applications ?? [])
    .map((link) => {
      const app = (link as { application?: { id?: number; internalNumber?: string; status?: string } }).application;
      if (!app?.id) return null;
      if (String(app.status || '').toUpperCase() === 'CANCELLED') return null;
      return {
        applicationId: app.id,
        label: app.internalNumber ? `#${app.internalNumber}` : `#${app.id}`,
      };
    })
    .filter(Boolean) as Array<{ applicationId: number; label: string }>;

  const renderAllocationMode = (expense: RouteExpense) => {
    const mode = String(expense.allocationMode || 'UNALLOCATED').toUpperCase();
    if (mode === 'UNALLOCATED') {
      return <Tag color="default">Не распределён</Tag>;
    }
    if (mode === 'COMMON') {
      return <Tag color="blue">Общий</Tag>;
    }
    if (mode === 'DIRECT') {
      const target =
        allocationCandidates.find((c) => c.applicationId === expense.directApplicationId)?.label ??
        (expense.directApplicationId ? `#${expense.directApplicationId}` : '—');
      return <Tag color="purple">Заявка {target}</Tag>;
    }
    return <Tag>{mode}</Tag>;
  };

  const body = (
    <>
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Направление: </Text>
          <Text>{formatRouteDirection(route.loadingAddress || '', route.unloadingAddress || '')}</Text>
        </div>

        {route.applications?.length ? (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Заявки: </Text>
            <RouteApplicationsCompactText applications={route.applications as any} />
          </div>
        ) : null}

        {route.applications?.length ? (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Клиент: </Text>
            <RouteClientsCompactText
              applications={route.applications as any}
              fallbackClient={route.client ?? null}
            />
          </div>
        ) : null}

        {route.carrierContractRequestNumber && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Договор-заявка: </Text>
            <Text>
              {route.carrierContractRequestNumber}
              {route.carrierContractRequestDate &&
                ` от ${dayjs(route.carrierContractRequestDate).format('DD.MM.YYYY')}`}
            </Text>
          </div>
        )}

        <Row gutter={16}>
          <Col span={24}>
            <Card
              size="small"
              title={
                <Space>
                  <ArrowUpOutlined style={{ color: '#ff4d4f' }} />
                  <span>Оплатить перевозчику</span>
                </Space>
              }
              style={{ marginBottom: 16, backgroundColor: '#fff7e6' }}
            >
              <FinanceSourceDataHint domain="route" />
              {route.carrier &&
                String(route.executor || '').toUpperCase() !== 'COMPANY' &&
                String(route.executor || '').toUpperCase() !== 'OUR_COMPANY' &&
                (payObl.length > 0 || routeView.payable) ? (
                <>
                  <div style={{ fontSize: 12, marginBottom: 8 }}>
                    {prepPay && (
                      <div>
                        Предоплата: {obligationTriggerCodeLabel(prepPay.triggerCode)}, Отсрочка {prepPay.delayDays ?? '—'} дн.
                      </div>
                    )}
                    {finalPay && (
                      <div>
                        Доплата: {obligationTriggerCodeLabel(finalPay.triggerCode)}, Отсрочка {finalPay.delayDays ?? '—'} дн.
                      </div>
                    )}
                  </div>
                  {routeView.payable && routeView.payable.totalRequired > 0 && (
                    <>
                      <Progress
                        percent={Math.min(
                          100,
                          Math.round((routeView.payable.totalPaid / routeView.payable.totalRequired) * 100)
                        )}
                        size="small"
                        showInfo={false}
                        strokeColor={routeView.payable.remaining > 0 ? '#faad14' : '#52c41a'}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        <MoneyCell value={routeView.payable.totalPaid} type="expense" /> /{' '}
                        <MoneyCell value={routeView.payable.totalRequired} type="expense" />
                      </Text>
                      {routeView.payable.remaining > 0 && (
                        <div style={{ marginTop: 4 }}>
                          <Text strong>
                            К оплате: <MoneyCell value={routeView.payable.remaining} type="expense" />
                          </Text>
                        </div>
                      )}
                    </>
                  )}
                  {payObl.filter((o) => o.id).map((obl) => (
                    <ObligationPaymentsBlock
                      key={obl.id}
                      obligation={obl}
                      visible={visible}
                      moneyType="expense"
                      onAddPayment={() => {
                        setPaymentModalDirection('EXPENSE');
                        setInitialObligationId(obl.id ?? null);
                        setCreatePaymentModalVisible(true);
                      }}
                    />
                  ))}
                  {(!routeView.payable || routeView.payable.totalRequired === 0) && payObl.filter((o) => o.id).length === 0 && (
                    <Text type="secondary">Нет данных</Text>
                  )}
                </>
              ) : (
                <Text type="secondary">Свой транспорт или нет данных</Text>
              )}
            </Card>
          </Col>
        </Row>

        <Card
          size="small"
          title={<Space>Расходы маршрута</Space>}
          extra={
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingExpense(null);
                setCreateExpenseModalVisible(true);
              }}
              disabled={!canCreateExpense}
            >
              Добавить расход
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          {loadingExpenses ? (
            <Text type="secondary">Загрузка...</Text>
          ) : (
            <>
              <DataTable<RouteExpense>
                size="small"
                dataSource={routeExpenses}
                rowKey="id"
                pagination={false}
                columns={[
                  {
                    title: 'Тип',
                    dataIndex: 'type',
                    key: 'type',
                    render: (type: string) => routeExpenseTypeLabel(type),
                  },
                  {
                    title: 'Описание',
                    dataIndex: 'description',
                    key: 'description',
                    ellipsis: true,
                    render: (d: string | null) => d || '—',
                  },
                  {
                    title: 'Дата',
                    dataIndex: 'expenseDate',
                    key: 'expenseDate',
                    width: 100,
                    render: (d: string) => (d ? dayjs(d).format('DD.MM.YYYY') : '—'),
                  },
                  {
                    title: 'Сумма',
                    dataIndex: 'amountWithVAT',
                    key: 'amountWithVAT',
                    align: 'right',
                    width: 110,
                    render: (v: number) => <MoneyCell value={Number(v ?? 0)} type="expense" />,
                  },
                  {
                    title: 'Счёт',
                    key: 'invoice',
                    width: 120,
                    render: (_, r) => (r.invoice ? `${r.invoice.number || r.invoice.id}` : '—'),
                  },
                  {
                    title: 'Статус',
                    dataIndex: 'status',
                    key: 'status',
                    width: 120,
                    render: (status: string) => {
                      const st = String(status || '').toUpperCase();
                      if (st === 'DRAFT') return <Tag color="gold">Черновик</Tag>;
                      if (st === 'CONFIRMED') return <Tag color="green">Подтвержден</Tag>;
                      if (st === 'REJECTED') return <Tag color="red">Отклонен</Tag>;
                      return <Tag>{st || '—'}</Tag>;
                    },
                  },
                  {
                    title: 'Распределение',
                    key: 'allocation',
                    width: 200,
                    render: (_, r) => {
                      const rows = r.allocations ?? [];
                      return (
                        <Space direction="vertical" size={2}>
                          {renderAllocationMode(r)}
                          {rows.length > 0 && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {rows.map((row) => (
                                <div key={row.applicationId}>
                                  #{row.applicationId}:{' '}
                                  <MoneyCell value={Number(row.allocatedAmount ?? 0)} type="expense" /> (
                                  {(Number(row.share) * 100).toFixed(1)}%)
                                </div>
                              ))}
                            </Text>
                          )}
                        </Space>
                      );
                    },
                  },
                  {
                    title: 'Действия',
                    key: 'actions',
                    width: 260,
                    render: (_, r) => {
                      const status = String(r.status || '').toUpperCase();
                      const allocationMode = String(r.allocationMode || 'UNALLOCATED').toUpperCase();
                      const canAllocate =
                        canConfirmReject &&
                        status === 'CONFIRMED' &&
                        allocationMode === 'UNALLOCATED';
                      if (status !== 'DRAFT') {
                        if (!canAllocate) {
                          return <Text type="secondary">Только просмотр</Text>;
                        }
                        return (
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => {
                              setAllocateExpense(r);
                              setAllocateMode('COMMON');
                              setAllocateApplicationId(allocationCandidates[0]?.applicationId ?? null);
                            }}
                          >
                            Распределить
                          </Button>
                        );
                      }
                      const isOwner = Number(r.createdBy) === Number(user?.id);
                      const canLogistEdit = role === 'logist' && isOwner;
                      const canDeleteDraft = role === 'admin' || role === 'accountant' || canLogistEdit;
                      const canSaveDraft = canLogistEdit;
                      const canAccountantConfirm = ['admin', 'accountant'].includes(role);
                      return (
                        <Space>
                          <Button
                            size="small"
                            disabled={!canSaveDraft}
                            onClick={() => {
                              setEditingExpense(r);
                              setCreateExpenseModalVisible(true);
                            }}
                          >
                            Редактировать
                          </Button>
                          <Button
                            size="small"
                            danger
                            disabled={!canDeleteDraft}
                            loading={deletingExpense}
                            onClick={async () => {
                              try {
                                await deleteExpense({ routeId: route.id, expenseId: r.id }).unwrap();
                                onRefetch?.();
                              } catch (e: any) {
                                message.error(e?.data?.error || 'Не удалось удалить расход');
                              }
                            }}
                          >
                            Удалить
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            disabled={!canAccountantConfirm}
                            loading={statusUpdating}
                            onClick={async () => {
                              try {
                                await setExpenseStatus({ routeId: route.id, expenseId: r.id, status: 'CONFIRMED' }).unwrap();
                                onRefetch?.();
                              } catch (e: any) {
                                message.error(e?.data?.error || 'Не удалось подтвердить расход');
                              }
                            }}
                          >
                            Подтвердить
                          </Button>
                          <Button
                            size="small"
                            disabled={!canAccountantConfirm}
                            loading={statusUpdating}
                            onClick={async () => {
                              try {
                                await setExpenseStatus({ routeId: route.id, expenseId: r.id, status: 'REJECTED' }).unwrap();
                                onRefetch?.();
                              } catch (e: any) {
                                message.error(e?.data?.error || 'Не удалось отклонить расход');
                              }
                            }}
                          >
                            Отклонить
                          </Button>
                        </Space>
                      );
                    },
                  },
                ]}
              />
              {routeExpenses.length === 0 && <Text type="secondary">Расходов пока нет</Text>}
              <div style={{ marginTop: 12, textAlign: 'right' }}>
                <Text strong>
                  Итого дополнительных расходов: <MoneyCell value={totalExpenses} type="expense" />
                </Text>
              </div>
            </>
          )}
        </Card>

        <Card size="small" title={<Space><FileTextOutlined />Документы</Space>}>
          <Space>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => setDocumentsModalVisible(true)}
            >
              Открыть документы маршрута
            </Button>
            <Button
              type="default"
              icon={<DollarOutlined />}
              onClick={() => {
                setPaymentModalDirection('EXPENSE');
                setInitialObligationId(null);
                setCreatePaymentModalVisible(true);
              }}
            >
              Создать платеж перевозчику
            </Button>
          </Space>
        </Card>
    </>
  );

  return (
    <>
      {embedded ? (
        body
      ) : (
        <Modal
          title={
            <Space>
              <DollarOutlined />
              <span>Маршрут #{route.id}</span>
              {routeView.aggregatedStatus && (
                <StatusCell status={routeView.aggregatedStatus.overallStatus} />
              )}
            </Space>
          }
          open={visible}
          onCancel={onClose}
          footer={null}
          width={700}
        >
          {body}
        </Modal>
      )}

      <CreatePaymentModal
        visible={createPaymentModalVisible}
        routeId={route.id}
        routeApplicationId={route.applications?.[0]?.application?.id}
        initialObligationId={initialObligationId}
        obligationDirectionFilter={paymentModalDirection}
        onClose={() => {
          setCreatePaymentModalVisible(false);
          setPaymentModalDirection(null);
          setInitialObligationId(null);
        }}
        onSuccess={() => {
          setCreatePaymentModalVisible(false);
          setPaymentModalDirection(null);
          setInitialObligationId(null);
          onRefetch?.();
        }}
      />

      <Modal
        title="Распределить расход"
        open={Boolean(allocateExpense)}
        onCancel={() => {
          setAllocateExpense(null);
          setAllocateMode('COMMON');
          setAllocateApplicationId(null);
        }}
        confirmLoading={allocatingExpense}
        okText="Сохранить"
        cancelText="Отмена"
        okButtonProps={{
          disabled:
            allocateMode === 'DIRECT' &&
            (allocateApplicationId == null || !Number.isFinite(allocateApplicationId)),
        }}
        onOk={async () => {
          if (!allocateExpense || !route?.id) return;
          try {
            await setExpenseAllocation({
              routeId: route.id,
              expenseId: allocateExpense.id,
              data:
                allocateMode === 'DIRECT'
                  ? {
                      allocationMode: 'DIRECT',
                      directApplicationId: Number(allocateApplicationId),
                    }
                  : { allocationMode: 'COMMON' },
            }).unwrap();
            message.success('Распределение сохранено');
            setAllocateExpense(null);
            onRefetch?.();
          } catch (e: any) {
            const code = e?.data?.code;
            if (code === 'OQ11_UNRESOLVED') {
              message.error('Невозможно распределить: некорректные ставки заявок (OQ-11)');
            } else if (code === 'OQ10_NOT_IMPLEMENTED') {
              message.error('Этот переход распределения не поддерживается (OQ-10)');
            } else {
              message.error(e?.data?.error || 'Не удалось распределить расход');
            }
          }
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Text type="secondary">
            Доступно только для подтверждённых расходов в статусе «Не распределён».
          </Text>
          <Radio.Group
            value={allocateMode}
            onChange={(e) => setAllocateMode(e.target.value)}
          >
            <Radio value="COMMON">На весь маршрут (COMMON)</Radio>
            <Radio value="DIRECT">На одну заявку (DIRECT)</Radio>
          </Radio.Group>
          {allocateMode === 'DIRECT' && (
            <Select
              style={{ width: '100%' }}
              placeholder="Выберите заявку"
              value={allocateApplicationId ?? undefined}
              options={allocationCandidates.map((c) => ({
                value: c.applicationId,
                label: c.label,
              }))}
              onChange={(v) => setAllocateApplicationId(v)}
            />
          )}
        </Space>
      </Modal>

      <CreateRouteExpenseModal
        visible={createExpenseModalVisible}
        routeId={route.id}
        editExpense={editingExpense}
        onClose={() => {
          setCreateExpenseModalVisible(false);
          setEditingExpense(null);
        }}
        onSuccess={() => {
          setCreateExpenseModalVisible(false);
          setEditingExpense(null);
          onRefetch?.();
        }}
      />

      <RouteDocumentsModal
        visible={documentsModalVisible}
        route={route}
        onClose={() => {
          setDocumentsModalVisible(false);
          onRefetch?.();
        }}
        onChanged={onRefetch}
      />
    </>
  );
};

interface FinanceRouteModalProps {
  visible: boolean;
  route: ExtendedRoute | null;
  onClose: () => void;
  onRefetch?: () => void;
}

const FinanceRouteModal: React.FC<FinanceRouteModalProps> = (props) => (
  <FinanceRouteFinanceContent {...props} embedded={false} />
);

export default FinanceRouteModal;
